import * as React from 'react';
import { useState } from 'react';
import { Button, Input, Tooltip, message } from 'antd';
import { CopyOutlined, DislikeOutlined, LikeOutlined } from '@ant-design/icons';
import OWButton from '../../../components/ow_button/OWButton';
import { cn } from '../../../utils/ClassNamesUtils';
import NotesMediaFilterPill from '../../../components/notes_media_grid/NotesMediaFilterPill';
import { useOscarRole, useOscarTheme } from '../../context/OscarRoleContext';
import { AgentFeedbackReason, AgentMessageFeedback } from '../../../types/agentHarness';

const FEEDBACK_REASONS: { reason: AgentFeedbackReason; label: string }[] = [
	{ reason: 'not_useful', label: 'Not useful' },
	{ reason: 'not_related', label: 'Not related' },
	{ reason: 'other', label: 'Other' },
];

const SAVE_ERROR_MESSAGE = 'Could not save your feedback. Please try again.';
const COPY_ERROR_MESSAGE = 'Could not copy the answer.';

/** Mirrors the harness's limit on a feedback comment. */
const COMMENT_MAX_LENGTH = 1000;

interface OscarMessageActionsProps {
	/** The answer's text, as the copy button puts it on the clipboard. */
	text: string;
	/** `useAgentChat().sendFeedback` bound to this answer. Omit to show copy only. */
	onSendFeedback?: (feedback: AgentMessageFeedback) => Promise<void>;
}

/**
 * The row under a settled Oscar answer: copy, good response, bad response. A
 * thumbs down is saved at once, so skipping the reason form still records it;
 * the form then asks what went wrong, and `Other` requires an explanation.
 *
 * The rating lives here only, so it is not shown again after a reload — the
 * harness stores it, but history does not replay it.
 */
export const OscarMessageActions = ({ text, onSendFeedback }: OscarMessageActionsProps) => {
	const theme = useOscarTheme();
	const role = useOscarRole();
	const [rating, setRating] = useState<AgentMessageFeedback['rating']>(null);
	const [copied, setCopied] = useState(false);
	const [reasonFormOpen, setReasonFormOpen] = useState(false);
	const [reason, setReason] = useState<AgentFeedbackReason>(null);
	const [comment, setComment] = useState('');
	// One save at a time, so a rating that lands late cannot overtake a newer one.
	const [saving, setSaving] = useState(false);
	// The reason form's own Submit. Only this locks the form: the bare thumbs down saved
	// as the form opens must not swallow a reason picked while it is still in flight.
	const [submitting, setSubmitting] = useState(false);

	const save = (feedback: AgentMessageFeedback) => {
		setSaving(true);
		return onSendFeedback(feedback).finally(() => setSaving(false));
	};

	const resetReasonForm = () => {
		setReasonFormOpen(false);
		setReason(null);
		setComment('');
	};

	const copy = () => {
		// Missing outside a secure context; say so rather than doing nothing.
		if (!navigator.clipboard) {
			message.error(COPY_ERROR_MESSAGE);
			return;
		}
		navigator.clipboard.writeText(text).then(
			() => setCopied(true),
			() => message.error(COPY_ERROR_MESSAGE)
		);
	};

	const handleCopyTooltipChange = (visible: boolean) => {
		if (!visible) setCopied(false);
	};

	const rateGood = () => {
		if (rating === 'positive') return;
		resetReasonForm();
		save({ rating: 'positive' })
			.then(() => setRating('positive'))
			.catch(() => message.error(SAVE_ERROR_MESSAGE));
	};

	const rateBad = () => {
		setReasonFormOpen(true);
		if (rating === 'negative') return;
		save({ rating: 'negative' })
			.then(() => setRating('negative'))
			.catch(() => message.error(SAVE_ERROR_MESSAGE));
	};

	// The pill has no disabled state; a submit in flight must not change what it sends.
	const toggleReason = (selected: string) => {
		if (submitting) return;
		setReason(reason === selected ? null : (selected as AgentFeedbackReason));
	};

	const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) =>
		setComment(event.target.value);

	const trimmedComment = comment.trim();
	const explanationRequired = reason === 'other';

	const submitReason = () => {
		setSubmitting(true);
		save({
			rating: 'negative',
			...(reason ? { reason } : {}),
			...(trimmedComment ? { comment: trimmedComment } : {}),
		})
			.then(() => {
				setRating('negative');
				resetReasonForm();
			})
			.catch(() => message.error(SAVE_ERROR_MESSAGE))
			.finally(() => setSubmitting(false));
	};

	const ratingButtonClassName = (selected: boolean) =>
		cn('flex items-center justify-center', selected && theme.selectedAction);

	return (
		<div className="flex w-full flex-col gap-s">
			<div className="flex items-center gap-2xs">
				<Tooltip title={copied ? 'Copied' : 'Copy'} onVisibleChange={handleCopyTooltipChange}>
					<Button
						type="text"
						aria-label="Copy"
						className={ratingButtonClassName(false)}
						icon={<CopyOutlined />}
						onClick={copy}
					/>
				</Tooltip>
				{onSendFeedback ? (
					<React.Fragment>
						<Tooltip title="Good response">
							<OWButton
								type="text"
								aria-label="Good response"
								aria-pressed={rating === 'positive'}
								className={ratingButtonClassName(rating === 'positive')}
								icon={<LikeOutlined />}
								disabled={saving}
								onClick={rateGood}
							/>
						</Tooltip>
						<Tooltip title="Bad response">
							<OWButton
								type="text"
								aria-label="Bad response"
								aria-pressed={rating === 'negative'}
								className={ratingButtonClassName(rating === 'negative')}
								icon={<DislikeOutlined />}
								disabled={saving}
								onClick={rateBad}
							/>
						</Tooltip>
					</React.Fragment>
				) : null}
			</div>
			{reasonFormOpen ? (
				<div className="flex flex-col gap-s rounded border border-solid border-grey-4 bg-white p-m shadow-notifications-card">
					<span className="bodymedium text-character-primary">What did we get wrong?</span>
					<div className="flex flex-wrap gap-xs">
						{FEEDBACK_REASONS.map((option) => (
							<NotesMediaFilterPill
								key={option.reason}
								value={option.reason}
								label={option.label}
								isActive={reason === option.reason}
								userType={role}
								onSelect={toggleReason}
							/>
						))}
					</div>
					<div className="flex flex-col gap-2xs">
						{explanationRequired ? (
							<span className="bodyregular text-character-primary">
								<span className="text-character-danger">* </span>
								Please explain
							</span>
						) : null}
						<Input
							placeholder={explanationRequired ? undefined : 'Tell us more (optional)'}
							aria-label={explanationRequired ? 'Please explain' : 'Tell us more (optional)'}
							maxLength={COMMENT_MAX_LENGTH}
							disabled={submitting}
							value={comment}
							onChange={handleCommentChange}
						/>
					</div>
					<div className="flex justify-end gap-xs">
						<Button onClick={resetReasonForm}>Skip</Button>
						<OWButton
							type="primary"
							loading={submitting}
							// Any save in flight, so a submit never races the first thumbs down.
							disabled={saving || (explanationRequired && !trimmedComment)}
							onClick={submitReason}
						>
							Submit
						</OWButton>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default OscarMessageActions;
